import React from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { styled } from '@mui/material/styles';
import {
    Box,
    Skeleton,
    Tooltip,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';

import { StyledListItemButton } from 'common/components';

const StyledWrapper = styled(Box)`
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    background-color: ${({ theme }) => theme.palette.secondary.gray};
`;

const IconColumn = styled(Box)(
    ({ theme }) =>
        `
        align-items: center;
        background: ${theme.palette.primary.main};
        border-bottom-left-radius: ${theme.shape.borderRadius}px;
        border-top-left-radius: ${theme.shape.borderRadius}px;
        display: flex;
        justify-content: center;
        padding: 8px;
        svg path { fill: ${theme.palette.primary.contrastText}; }
        `,
);

const getVariationIcon = (variation) => {
    if (variation > 0) return <ArrowDropUpIcon sx={{ color: '#3CAF5F' }} />;
    if (variation < 0) return <ArrowDropDownIcon sx={{ color: '#DA1E28' }} />;
    return null;
};

const KeyIndicator = ({
    extra,
    infoTitle,
    isLoading,
    onClick,
    preffix,
    svgIcon,
    sxContent,
    title,
    to,
    unit,
    value,
    variation,
    variationValue,
    widthIcon = '40px',
}) => {
    const displayPreffix = preffix ? <span>{preffix}</span> : null;
    const displayUnit = unit ? <span>{unit}</span> : null;

    const component = to ? RouterLink : null;
    const Wrapper = to ? StyledListItemButton : StyledWrapper;

    return (
        <Wrapper component={component} onClick={onClick} to={to} sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', flexGrow: 1 }}>
                <IconColumn sx={{ width: widthIcon }}>{svgIcon}</IconColumn>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        p: 1,
                        width: '100%',
                    }}
                >
                    {isLoading ? (
                        <Box display="flex" flexDirection="column">
                            <Skeleton
                                variant="text"
                                sx={{ fontSize: '1rem' }}
                            />
                            <Skeleton
                                variant="text"
                                sx={{ fontSize: '1rem', width: '100px' }}
                            />
                        </Box>
                    ) : (
                        <Box display="flex" gap={1}>
                            <Box sx={sxContent}>
                                <Box display="flex" gap={1}>
                                    <Typography variant="caption">
                                        {title}
                                    </Typography>
                                    {infoTitle && (
                                        <Tooltip
                                            placement="top"
                                            title={infoTitle}
                                        >
                                            <QuestionMarkIcon
                                                fontSize="10px"
                                                sx={{
                                                    backgroundColor:
                                                        'secondary.main',
                                                    borderRadius: '50%',
                                                    color: 'secondary.contrastText',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>

                                <Box alignItems="center" display="flex" gap={1}>
                                    <Typography
                                        fontWeight="bold"
                                        variant="body2"
                                    >
                                        {displayPreffix} {value} {displayUnit}
                                    </Typography>

                                    <Box alignItems="center" display="flex">
                                        <Typography variant="caption">
                                            {variation > 0 ? '+' : ''}
                                            {variationValue}
                                            {variationValue ? ' %' : ''}
                                        </Typography>

                                        {getVariationIcon(variation)}
                                    </Box>
                                </Box>
                            </Box>
                            {extra && (
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        ml: 'auto',
                                    }}
                                >
                                    {extra}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Wrapper>
    );
};

KeyIndicator.propTypes = {
    extra: PropTypes.node,
    infoTitle: PropTypes.string,
    isLoading: PropTypes.bool,
    onClick: PropTypes.func,
    preffix: PropTypes.string,
    svgIcon: PropTypes.object,
    sxContent: PropTypes.object,
    title: PropTypes.string,
    to: PropTypes.string,
    unit: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    variation: PropTypes.number,
    variationValue: PropTypes.string,
    widthIcon: PropTypes.string,
};

export default KeyIndicator;
